// md-editor-v3 扩展动态加载模块
// 将 katex、mermaid、prettier、cropper 等重依赖改为按需异步加载
//
// 【关键约束】md-editor-v3 在组件 setup() 阶段就把 config.editorExtensions.*.instance
// 快照进 shallowRef（见 lib/es/chunks/index3.mjs 中 katex/mermaid 扩展的实现），
// 之后不再回读 config。若实例在组件创建时缺失，该组件会把 LaTeX/流程图源码原样输出，
// 且不会自行恢复（库内置的重渲染闸门只监听这个快照 ref，而 CDN URL 已被置空，
// window.katex / window.mermaid 永远不会出现）。
// 因此调用方必须先 await ensureKatex()/ensureMermaid()，再创建 MdPreview/MdEditor 组件。

import { config } from "md-editor-v3/lib/es/config.mjs";

// 缓存加载 Promise，避免重复加载；失败时清空缓存以便下次重试
const once = (factory: () => Promise<void>) => {
  let promise: Promise<void> | null = null;
  return (): Promise<void> => {
    if (promise === null) {
      promise = factory().catch((e) => {
        promise = null;
        throw e;
      });
    }
    return promise;
  };
};

// mermaid 实例是否已注入 config：它体积接近 2MB，预览侧要按内容决定是否需要
let mermaidLoaded = false;

/**
 * 加载 KaTeX 并注入实例
 * MdPreview 与 MdEditor 的预览面板都依赖它渲染数学公式，是预览路径的必需扩展
 */
export const ensureKatex = once(async () => {
  const [{ default: katex }] = await Promise.all([
    import("katex"),
    // 动态加载 katex CSS，避免静态导入把样式带进首屏
    import("katex/dist/katex.min.css"),
  ]);

  config({
    editorExtensions: {
      katex: {
        instance: katex,
      },
    },
  });

  console.log("Katex extension loaded");
});

/**
 * 加载 Mermaid 并注入实例
 * 只在内容确实包含 mermaid 代码块时调用，避免为普通预览页面付出约 2MB 的代价
 * （调用点见 components/markdown/MdPreviewLazy.vue）
 */
export const ensureMermaid = once(async () => {
  // 注意：import() 返回模块命名空间对象，不能按数组解构（否则运行时报 not iterable）
  const { default: mermaid } = await import("mermaid");

  config({
    editorExtensions: {
      mermaid: {
        instance: mermaid,
      },
    },
  });

  mermaidLoaded = true;
  console.log("Mermaid extension loaded");
});

/** mermaid 实例是否已注入 config（已注入则后续挂载的组件无需再等待） */
export const isMermaidLoaded = () => mermaidLoaded;

/**
 * 加载编辑器专属的重依赖（prettier / cropper，含 mermaid）并注入实例
 *
 * prettier 与 cropper 的实例只在工具栏动作时读取，允许晚于组件创建注入，
 * 因此不阻塞 MdEditor 渲染：在进入含编辑器的页面时后台预热即可。
 * mermaid 与 katex 一样是 setup 阶段的快照，这里复用 ensureMermaid 的同一份加载。
 */
export const ensureEditorExtensions = once(async () => {
  const [, { default: Cropper }, { default: prettier }, { default: parserMarkdown }] =
    await Promise.all([
      ensureMermaid(),
      import("cropperjs"),
      import("prettier"),
      import("prettier/plugins/markdown"),
    ]);

  // 动态加载 cropper CSS
  await import("cropperjs/dist/cropper.css");

  // 裁剪弹窗在自身 setup 阶段只读一次 cropper.instance，晚注入对它无效；
  // 但它在弹窗打开时会回退读取 window.Cropper（MdEditor.mjs 中 `i = i || window.Cropper`），
  // 这里补上该全局引用，保证冷启动首次打开裁剪弹窗也能正常工作。
  window.Cropper = Cropper;

  config({
    editorExtensions: {
      prettier: {
        prettierInstance: prettier,
        parserMarkdownInstance: parserMarkdown,
      },
      cropper: {
        instance: Cropper,
      },
    },
  });

  console.log("Editor extensions loaded (prettier, cropper)");
});

/**
 * 立即触发后台预加载（不阻塞），用于含 md-editor-v3 的页面提前预热
 */
export const preloadEditorExtensions = () => {
  // katex 供编辑器预览渲染公式，其余供工具栏（流程图、格式化、图片裁剪）使用
  ensureKatex().catch(() => {});
  ensureEditorExtensions().catch(() => {});
};
